import * as Headless from '@headlessui/react';
import { ChevronDownIcon } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
} from '@/components/ui/catalyst/dropdown';
import { Input } from '@/components/ui/catalyst/input';
import { cn } from '@/lib/utils';
import { useGetTaskStatusesQuery } from '@/services/api';
import { Task, TaskStatus } from '@/services/types/client-intake-types';
import {
  CanonicalTaskStatus,
  TaskStatusColors,
} from '@/services/types/task-types';
import { filterTaskStatusesByGroup } from '@/utils/taskStatusUtils';

// Define the ordering for canonical task statuses
const getCanonicalStatusOrder = (status: CanonicalTaskStatus): number => {
  const order = [
    CanonicalTaskStatus.NOT_APPLICABLE,
    CanonicalTaskStatus.PENDING,
    CanonicalTaskStatus.IN_PROGRESS,
    CanonicalTaskStatus.COMPLETED,
    CanonicalTaskStatus.CLOSED,
    CanonicalTaskStatus.BLOCKED,
  ];

  return order.indexOf(status);
};

interface TaskStatusBadgeProps {
  status: TaskStatus;
  onStatusChange: (status: TaskStatus) => void;
  onCloseRequested?: (status: TaskStatus) => void;
  className?: string;
  task?: Task | null;
}

export const TaskStatusBadge = React.memo<TaskStatusBadgeProps>(
  ({ status, onStatusChange, onCloseRequested, className, task }) => {
    const { data: _statuses } = useGetTaskStatusesQuery();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    const statuses = useMemo(() => {
      if (!_statuses) return [];
      const filtered = filterTaskStatusesByGroup(_statuses, task);
      return [...filtered].sort(
        (a, b) =>
          getCanonicalStatusOrder(a.canonical_status) -
          getCanonicalStatusOrder(b.canonical_status),
      );
    }, [_statuses, task]);

    // Filter statuses based on search query
    const filteredStatuses = useMemo(
      () =>
        statuses?.filter((statusOption) =>
          statusOption.name.toLowerCase().includes(searchQuery.toLowerCase()),
        ) || [],
      [statuses, searchQuery],
    );

    // Reset selected index when search query changes
    useEffect(() => {
      setSelectedIndex(0);
    }, [searchQuery]);

    // Scroll selected item into view
    useEffect(() => {
      const selectedItem = itemRefs.current[selectedIndex];
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, [selectedIndex]);

    const handleStatusSelect = useCallback(
      (statusOption: TaskStatus) => {
        setSearchQuery('');

        // Intercept "Closed" status to show modal
        if (
          statusOption.canonical_status === CanonicalTaskStatus.CLOSED &&
          onCloseRequested
        ) {
          onCloseRequested(statusOption);
        } else if (onStatusChange) {
          onStatusChange(statusOption);
        }

        setSelectedIndex(0);
      },
      [onStatusChange, onCloseRequested],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setSelectedIndex((prev) =>
              prev < filteredStatuses.length - 1 ? prev + 1 : prev,
            );
            break;
          case 'ArrowUp':
            e.preventDefault();
            setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
            break;
          case 'Enter':
            e.preventDefault();
            if (filteredStatuses[selectedIndex]) {
              handleStatusSelect(filteredStatuses[selectedIndex]);
            }
            break;
          default:
            break;
        }
      },
      [filteredStatuses, selectedIndex, handleStatusSelect],
    );

    // Focus input after dropdown menu renders
    const handleMenuMount = useRef<boolean>(false);

    const menuRefCallback = useCallback((node: HTMLDivElement | null) => {
      if (node && !handleMenuMount.current) {
        handleMenuMount.current = true;
        requestAnimationFrame(() => {
          searchInputRef.current?.focus();
        });
      } else if (!node) {
        handleMenuMount.current = false;
      }
    }, []);

    const handleSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
      },
      [],
    );

    const handleItemMouseEnter = useCallback((index: number) => {
      setSelectedIndex(index);
    }, []);

    const stopPropagation = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    return (
      <Dropdown>
        <DropdownButton
          as="span"
          onClick={stopPropagation}
          onMouseDown={(e) => e.stopPropagation()}
          className={cn(
            `min-w-[80px] flex flex-row justify-between items-center gap-1 px-1.5 py-1 rounded text-[9px] border font-bold cursor-pointer transition-all hover:brightness-90 ${TaskStatusColors[status.canonical_status]}`,
            className,
          )}
        >
          {status.name}
          <ChevronDownIcon size={10} />
        </DropdownButton>
        <DropdownMenu
          anchor="bottom start"
          className="min-w-[200px] max-h-[300px] overflow-hidden flex-col flex! space-y-2 z-50"
          onClick={stopPropagation}
        >
          <div ref={menuRefCallback}>
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Search status..."
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              className="w-full text-xs"
              autoComplete="off"
            />
            <div className="overflow-y-auto max-h-[240px] flex flex-col gap-1 no-scrollbar">
              {filteredStatuses.length === 0 ? (
                <div className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
                  No statuses found
                </div>
              ) : (
                filteredStatuses.map((statusOption, index) => (
                  <Headless.MenuItem
                    key={statusOption.id}
                    as="button"
                    onClick={() => handleStatusSelect(statusOption)}
                    className="w-full"
                  >
                    <div
                      ref={(el) => {
                        itemRefs.current[index] = el;
                      }}
                      onMouseEnter={() => handleItemMouseEnter(index)}
                      className={cn(
                        'cursor-pointer px-2 py-1 mx-1 rounded-sm font-bold text-xs transition-all',
                        TaskStatusColors[statusOption.canonical_status],
                        selectedIndex === index &&
                          'ring-2 ring-blue-500 ring-offset-1',
                        'hover:brightness-90',
                      )}
                    >
                      {statusOption.name}
                    </div>
                  </Headless.MenuItem>
                ))
              )}
            </div>
          </div>
        </DropdownMenu>
      </Dropdown>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    return (
      prevProps.status.id === nextProps.status.id &&
      prevProps.status.name === nextProps.status.name &&
      prevProps.status.canonical_status === nextProps.status.canonical_status &&
      prevProps.className === nextProps.className &&
      prevProps.onStatusChange === nextProps.onStatusChange &&
      prevProps.task?.id === nextProps.task?.id &&
      prevProps.task?.group?.canonical_group ===
        nextProps.task?.group?.canonical_group
    );
  },
);

TaskStatusBadge.displayName = 'TaskStatusBadge';
